import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
} from "@chakra-ui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Layout } from "components/atoms";
import {
  AgentCnpjInput,
  InputBuild,
  SelectBuild,
} from "components/molecules";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { formFilter } from "./form";
import { FilterFee } from "./interfaces";
import { schema } from "./schema";

interface FilterFeeManagementProps {
  handleFilterFee: (value: FilterFee) => void;
  loadingFilter?: boolean;
  type: "price" | "tax" | "detran";
}

type FilterFeeField = keyof FilterFee;

export function FilterFeeManagement({
  handleFilterFee,
  loadingFilter,
  type,
}: FilterFeeManagementProps) {
  const formData = useMemo(() => formFilter(type), [type]);

  const {
    register,
    formState: { errors },
    watch,
    handleSubmit,
    reset,
    setValue,
  } = useForm<FilterFee>({
    resolver: yupResolver(schema),
    mode: "onChange",
    defaultValues:
      type === "detran"
        ? {
            uf: "RJ",
            numeroTaxa: "",
            cnpj: "",
            status: "",
          }
        : {
            uf: "",
            status: "",
            dataInicio: "",
            dataFim: "",
            cnpjAgenteFinanceiro: "",
            publica: "",
            tipoVeiculo: "",
          },
  });

  const clearDetran = (): FilterFee => {
    return {
      uf: "RJ",
      numeroTaxa: "",
      cnpj: "",
      status: "",
    };
  };

  const clearRegistrations = (): FilterFee => {
    return {
      uf: "",
      status: "",
      dataInicio: "",
      dataFim: "",
      cnpjAgenteFinanceiro: "",
      publica: "",
      tipoVeiculo: "",
    };
  };

  const clearByType = (type: "detran" | "price" | "tax"): FilterFee => {
    const options = {
      price: clearRegistrations(),
      tax: clearRegistrations(),
      detran: clearDetran(),
    };

    return options[type];
  };

  const handleClear = () => {
    reset(clearByType(type));
  };

  const onSubmit = (value: FilterFee) => {
    handleFilterFee(value);
  };

  const selectedUf = String(watch("uf") ?? "");

  return (
    <Accordion allowMultiple backgroundColor={"white"} marginBottom={4}>
      <AccordionItem>
        <h2>
          <AccordionButton backgroundColor={"white"} className="white-hover">
            <Box
              as="h2"
              flex="1"
              textAlign="left"
              fontSize={20}
              fontWeight={700}
            >
              Filtrar Taxa(s):
            </Box>
            <AccordionIcon />
          </AccordionButton>
        </h2>

        <AccordionPanel pb={4} backgroundColor={"white"}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Box backgroundColor={"white"}>
              <Layout type="row" gutter={{ y: 1.5 }}>
                {formData.map(({ layout, input, select }, key) => {
                  const selectId = select?.id as FilterFeeField;
                  const inputId = input?.id as FilterFeeField;
                  const isAgentCnpjInput =
                    inputId === "cnpj" ||
                    inputId === "cnpjAgenteFinanceiro";

                  return (
                    <Layout {...layout} key={key}>
                      {select ? (
                        <SelectBuild
                          {...select}
                          err={errors[selectId]}
                          {...register(selectId)}
                        />
                      ) : input && isAgentCnpjInput ? (
                        <>
                          <input type="hidden" {...register(inputId)} />

                          <AgentCnpjInput
                            id={input.id}
                            label={input.label ?? "CNPJ"}
                            uf={selectedUf}
                            value={String(watch(inputId) ?? "")}
                            err={errors[inputId]}
                            required={input.required}
                            placeholder={input.placeholder}
                            onChange={(cnpj) =>
                              setValue(inputId, cnpj, {
                                shouldDirty: true,
                                shouldTouch: true,
                                shouldValidate: true,
                              })
                            }
                          />
                        </>
                      ) : (
                        input && (
                          <InputBuild
                            {...input}
                            err={errors[inputId]}
                            {...register(inputId)}
                            value={String(watch(inputId) ?? "")}
                          />
                        )
                      )}
                    </Layout>
                  );
                })}
              </Layout>

              <Box
                paddingY={8}
                display="flex"
                alignItems="center"
                justifyContent="center"
                gap={6}
              >
                <Button
                  colorScheme="primary"
                  variant="outline"
                  onClick={handleClear}
                >
                  Limpar Campos
                </Button>

                <Button
                  colorScheme="primary"
                  type="submit"
                  isLoading={loadingFilter}
                >
                  Filtrar
                </Button>
              </Box>
            </Box>
          </form>
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );
}
